import { Spectral } from '@stoplight/spectral-core';
import { ValidationOutput } from './validation.output.js';
import { SpectralResult } from './spectral.result.js';
import { selectChoices, extractOptions } from '../generate/components/options.js';
import { initLogger } from '../../logger.js';
/**
 * Pure, stateless helpers shared by the validation orchestrator (`validate.ts`) and the
 * individual {@link ValidationRule} implementations. Kept in a dependency-free module so the
 * rules can import them without creating an import cycle back through `validate.ts`.
 */
export function prettifyJson(json) {
    return JSON.stringify(json, null, 4);
}
export function stripRefs(obj) {
    return JSON.stringify(obj).replaceAll('$ref', 'ref');
}
export function sortSpectralIssueBySeverity(issues) {
    issues.sort((issue1, issue2) => issue1.severity.valueOf() - issue2.severity.valueOf());
}
/**
 * Run a single Spectral ruleset against a stringified document.
 */
export async function runSpectralValidations(schema, spectralRuleset, source, logger = initLogger(false, 'calm-validate')) {
    let errors = false;
    let warnings = false;
    let spectralIssues = [];
    const spectral = new Spectral();
    spectral.setRuleset(spectralRuleset);
    const issues = await spectral.run(schema);
    if (issues && issues.length > 0) {
        logger.debug(`Spectral raw output: ${prettifyJson(issues)}`);
        sortSpectralIssueBySeverity(issues);
        spectralIssues = convertSpectralDiagnosticToValidationOutputs(issues, source);
        if (issues.filter(issue => issue.severity === 0).length > 0) {
            logger.debug('Spectral output contains errors');
            errors = true;
        }
        if (issues.filter(issue => issue.severity === 1).length > 0) {
            logger.debug('Spectral output contains warnings');
            warnings = true;
        }
    }
    return new SpectralResult(warnings, errors, spectralIssues);
}
/**
 * Apply the answers recorded in the architecture to the pattern, as `calm generate` does, so the
 * architecture is checked against the pattern it was built from. An empty answer counts too: it
 * drops the slot of its alternatives.
 */
export function applyArchitectureOptionsToPattern(architecture, pattern, debug) {
    if (!relationshipsOf(architecture).some(isDecision)) {
        return pattern;
    }
    const narrowed = selectChoices(pattern, extractChoicesFromArchitecture(architecture), debug);
    dropEmptyPrefixItems(narrowed);
    return narrowed;
}
// Narrowing can leave `prefixItems: []`, which JSON Schema forbids. No `prefixItems` means the same.
function dropEmptyPrefixItems(schema) {
    if (!schema || typeof schema !== 'object') {
        return;
    }
    const node = schema;
    if (Array.isArray(node['prefixItems']) && node['prefixItems'].length === 0) {
        delete node['prefixItems'];
    }
    Object.values(node).forEach(dropEmptyPrefixItems);
}
/**
 * A `oneOf` decision needs exactly one answer. An empty answer drops its slot like any other, so
 * without this check the architecture would pass with no answer.
 */
export function unansweredOneOfDecisions(architecture, pattern) {
    const unanswered = relationshipsOf(architecture)
        .map((rel, index) => ({ rel, index }))
        .filter(({ rel }) => isDecision(rel) && rel['relationship-type']['options'].length === 0);
    if (unanswered.length === 0) {
        return [];
    }
    const oneOfIds = new Set(extractOptions(pattern).filter(o => o.optionType === 'oneOf').map(o => o.optionId));
    return unanswered
        .filter(({ rel }) => oneOfIds.has(rel['unique-id']))
        .map(({ rel, index }) => ValidationOutput.error('json-schema', `The decision [${rel['unique-id']}] is a oneOf and needs exactly one answer.`, `/relationships/${index}/relationship-type/options`, { source: 'architecture' }));
}
function relationshipsOf(architecture) {
    const relationships = architecture?.['relationships'];
    return Array.isArray(relationships) ? relationships : [];
}
function isDecision(rel) {
    return Array.isArray(rel?.['relationship-type']?.['options']);
}
export function extractChoicesFromArchitecture(architecture) {
    return relationshipsOf(architecture)
        .filter(isDecision)
        // A decision answered with nothing records no choice.
        .filter((rel) => rel['relationship-type']['options'].length > 0)
        .map((rel) => rel['relationship-type']['options'][0])
        .map((rel) => ({
        description: rel['description'],
        nodes: rel['nodes'] || [],
        relationships: rel['relationships'] || []
    }));
}
/**
 * Finds the URL of the most recent CALM core schema loaded in the schema directory.
 *
 * Precedence: release > draft. Within each tier, URLs are compared with a numeric-aware locale
 * sort so that 1.10 > 1.9 > 1.2. Returns undefined if no CALM core schema is loaded.
 */
export function findLatestCalmCoreSchemaUrl(schemaDirectory) {
    const coreSchemaPattern = /calm\.finos\.org\/.*\/meta\/core\.json$/;
    const matches = schemaDirectory.getLoadedSchemas()
        .filter(id => coreSchemaPattern.test(id));
    if (matches.length === 0)
        return undefined;
    return matches.sort((a, b) => {
        const isRelease = (url) => url.includes('/release/');
        if (isRelease(a) !== isRelease(b))
            return isRelease(a) ? -1 : 1;
        return b.localeCompare(a, undefined, { numeric: true });
    })[0];
}
export function convertJsonSchemaIssuesToValidationOutputs(jsonSchemaIssues, source) {
    return jsonSchemaIssues.map(issue => {
        const rawPath = issue.instancePath ?? '';
        const path = rawPath === '' ? '/' : rawPath;
        return ValidationOutput.error('json-schema', appendExpected(issue), path, {
            schemaPath: issue.schemaPath,
            source
        });
    });
}
export function convertSpectralDiagnosticToValidationOutputs(spectralIssues, source) {
    const validationOutput = [];
    spectralIssues.forEach(issue => {
        const startRange = issue.range.start;
        const endRange = issue.range.end;
        const formattedIssue = new ValidationOutput(issue.code, getSeverity(issue.severity), issue.message, '/' + issue.path.join('/'), '', startRange.line, endRange.line, startRange.character, endRange.character, source);
        validationOutput.push(formattedIssue);
    });
    return validationOutput;
}
function getSeverity(spectralSeverity) {
    switch (spectralSeverity) {
        case 0:
            return 'error';
        case 1:
            return 'warning';
        case 2:
            return 'info';
        case 3:
            return 'hint';
        default:
            throw Error('The spectralSeverity does not match the known values');
    }
}
function appendExpected(issue) {
    if (!issue || !issue.params) {
        return issue?.message ?? '';
    }
    const params = issue.params;
    // const keyword: prefer params.allowedValue, fall back to schema (AJV sets schema to the const value)
    if (issue.keyword === 'const') {
        const allowed = 'allowedValue' in params ? params['allowedValue'] : issue.schema;
        if (allowed !== undefined) {
            return `${issue.message} (expected ${safeStringify(allowed)})`;
        }
    }
    // enum keyword: prefer params.allowedValues, fall back to schema array
    if (issue.keyword === 'enum') {
        const allowedValues = 'allowedValues' in params ? params['allowedValues'] : issue.schema;
        if (Array.isArray(allowedValues)) {
            return `${issue.message} (expected one of ${safeStringify(allowedValues)})`;
        }
    }
    return issue.message ?? '';
}
function safeStringify(value) {
    try {
        return JSON.stringify(value);
    }
    catch (_) {
        return String(value);
    }
}
//# sourceMappingURL=validation-helpers.js.map